import {Alert, Button, DataStatus, openDialog, showNotification} from 'platform/components';
import {Box, Center, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {useRef, useState, useSyncExternalStore} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  useGetConfigurationDetailQuery,
  useGetMakeQuery,
  useGetModelQuery,
  useGetModelYearQuery,
  useLazyGetConfigurationDetailQuery,
} from '@dms/api/new-vehicle';
import {
  getOrderConfigurationDialogId,
  getRegisterExternalOrderDialogId,
  OrderConfigurationDialog,
  RegisterExternalOrderDialog,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, Main} from '@dms/shared';

import {composePath, useLatest, useNavigate, useOnMount} from 'shared/web';

import {MakeDetail} from '../NewVehicleCatalogue/MakeDetail';
import {ModelDetail} from '../NewVehicleCatalogue/ModelDetail';
import {ModelYearShell} from '../NewVehicleCatalogue/ModelYearShell';
import {NewModelWizard} from '../NewVehicleCatalogue/NewModelWizard';
import {NewVehicleCatalogue} from '../NewVehicleCatalogue/NewVehicleCatalogue';
import {
  NewVehicleConfigurator,
  type ConfiguratorFormValues,
} from '../NewVehicleConfigurator/NewVehicleConfigurator';
import {configuratorDraftSchema} from '../NewVehicleConfigurator/utils/configuratorDraftSchema';
import {
  getPendingCreatedConfigurationKey,
  pendingCreatedConfigurationStore,
} from '../NewVehicleConfigurator/utils/pendingCreatedConfigurationStore';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

type ConfiguratorLoaderProps = {
  configurationId?: string;
  businessCaseId?: string;
  replaceSaleVehicleId?: string;
  interestId?: string;
  modelYearId?: string;
  trimId?: string;
  onOrderConfiguration?: (configurationId: string, isIntentCurrent: () => boolean) => void;
  onRegisterExternalOrder?: (configurationId: string, isIntentCurrent: () => boolean) => void;
};

function UserConfiguratorLoader(props: ConfiguratorLoaderProps & {userId: string}) {
  const navigate = useNavigate();
  const detailQuery = useGetConfigurationDetailQuery(
    {configurationId: props.configurationId ?? ''},
    {skip: !props.configurationId}
  );
  const detail = detailQuery.currentData;
  // A draft belongs to where it was started, so a Business Case or Interest never offers (and
  // then saves against itself) a draft begun somewhere else.
  const draftContext =
    props.businessCaseId || props.interestId
      ? `:${encodeURIComponent(props.businessCaseId ?? '')}:${encodeURIComponent(props.interestId ?? '')}`
      : '';
  const draftKey = `newVehicleDraft:${props.userId}${draftContext}`;
  const pendingCreatedConfigurationKey = getPendingCreatedConfigurationKey(
    draftKey,
    props.businessCaseId,
    props.interestId
  );
  useSyncExternalStore(
    pendingCreatedConfigurationStore.subscribe,
    pendingCreatedConfigurationStore.getRevision,
    pendingCreatedConfigurationStore.getServerRevision
  );
  const pendingCreatedConfiguration = props.configurationId
    ? {status: 'none' as const}
    : pendingCreatedConfigurationStore.getState(pendingCreatedConfigurationKey);
  const createdConfigurationId =
    pendingCreatedConfiguration.status === 'completed'
      ? pendingCreatedConfiguration.record.configurationId
      : undefined;
  const stored = !props.configurationId ? sessionStorage.getItem(draftKey) : null;
  const [draftChoice, setDraftChoice] = useState<'pending' | 'resume' | 'new'>(
    stored || pendingCreatedConfiguration.status !== 'none' ? 'pending' : 'new'
  );

  if (draftChoice === 'pending' || pendingCreatedConfiguration.status === 'uncertain') {
    return (
      <Center width="100%" minHeight="60vh">
        <Box
          width={120}
          backgroundColor="general.white"
          border="1px solid"
          borderColor="general.separator"
          borderRadius="large"
          boxShadow="elevation_2"
          padding={6}
        >
          <VStack spacing={5} align="center">
            <Box
              width={14}
              height={14}
              borderRadius="circular"
              backgroundColor="palettes.blue.10.100"
            >
              <Center width="100%" height="100%">
                <Icon value="action/history" size={7} color="palettes.blue.60.100" />
              </Center>
            </Box>
            <VStack spacing={1} align="center">
              <Heading size={3} align="center">
                {t('entity.newVehicle.labels.draftFound', 'Saved draft found')}
              </Heading>
              <Text size="small" color="secondary" align="center">
                {t(
                  'entity.newVehicle.labels.draftFoundIntro',
                  'You left a configuration unfinished. Pick up where you stopped or start from scratch.'
                )}
              </Text>
            </VStack>
            {pendingCreatedConfiguration.status === 'uncertain' ? (
              <VStack spacing={3} align="center">
                <Alert
                  variant="warning"
                  message={t(
                    'entity.newVehicle.notifications.saveOutcomeUnknown',
                    'The save outcome is unknown. Check the configuration list before deciding whether to discard this draft.'
                  )}
                />
                <HStack spacing={2}>
                  <Button
                    title={t('entity.newVehicle.actions.startNew', 'Start new')}
                    variant="secondary"
                    onClick={() => {
                      sessionStorage.removeItem(draftKey);
                      pendingCreatedConfigurationStore.discard(pendingCreatedConfigurationKey);
                      setDraftChoice('new');
                    }}
                  />
                  <Button
                    title={t('entity.newVehicle.actions.openList', 'Open in list')}
                    onClick={() => navigate(vehiclesRoutes.newVehicleConfigurations)}
                  />
                </HStack>
              </VStack>
            ) : (
              <HStack spacing={2}>
                <Button
                  title={t('entity.newVehicle.actions.startNew', 'Start new')}
                  variant="secondary"
                  onClick={() => {
                    sessionStorage.removeItem(draftKey);
                    pendingCreatedConfigurationStore.discard(pendingCreatedConfigurationKey);
                    setDraftChoice('new');
                  }}
                />
                <Button
                  title={t('entity.newVehicle.actions.resumeDraft', 'Resume draft')}
                  leftIcon="AV/play_arrow"
                  isDisabled={pendingCreatedConfiguration.status === 'pending'}
                  onClick={() => {
                    if (!createdConfigurationId) {
                      setDraftChoice('resume');
                      return;
                    }
                    navigate(
                      composePath(vehiclesRoutes.newVehicleConfigurator, {
                        params: {step: '5'},
                        queryParams: {
                          configurationId: createdConfigurationId,
                          businessCaseId: props.businessCaseId,
                          replaceSaleVehicleId: props.replaceSaleVehicleId,
                          interestId: props.interestId,
                        },
                      })
                    );
                  }}
                />
              </HStack>
            )}
          </VStack>
        </Box>
      </Center>
    );
  }

  let initialDraft: ConfiguratorFormValues | undefined;
  if (draftChoice === 'resume' && stored) {
    try {
      const rawDraft: unknown = JSON.parse(stored);
      const parsedDraft = configuratorDraftSchema.safeParse(rawDraft);
      const draftWithoutDiscount =
        !parsedDraft.success && typeof rawDraft === 'object' && rawDraft !== null
          ? configuratorDraftSchema.safeParse({...rawDraft, proposedDiscount: undefined})
          : parsedDraft;
      if (draftWithoutDiscount.success) {
        initialDraft = draftWithoutDiscount.data;
      } else {
        sessionStorage.removeItem(draftKey);
      }
    } catch {
      sessionStorage.removeItem(draftKey);
    }
  }

  return (
    <DataStatus
      isLoading={detailQuery.isLoading || (detailQuery.isFetching && !detail)}
      isError={detailQuery.isError && !detail}
    >
      <Show when={!(props.configurationId && !detail)}>
        <VStack spacing={3}>
          <Show when={detailQuery.isError && Boolean(detail)}>
            <Alert
              variant="error"
              message={t('general.labels.somethingWentWrong', 'Something went wrong.')}
            />
          </Show>
          <NewVehicleConfigurator
            detail={detail}
            draftKey={draftKey}
            initialDraft={initialDraft}
            businessCaseId={props.businessCaseId}
            replaceSaleVehicleId={props.replaceSaleVehicleId}
            interestId={props.interestId}
            onOrderConfiguration={props.onOrderConfiguration}
            onRegisterExternalOrder={props.onRegisterExternalOrder}
            deepLink={
              props.configurationId
                ? undefined
                : {modelYearId: props.modelYearId, trimId: props.trimId}
            }
          />
        </VStack>
      </Show>
    </DataStatus>
  );
}

function PreviewConfiguratorLoader(props: {modelYearId: string}) {
  const navigate = useNavigate();
  const yearQuery = useGetModelYearQuery({modelYearId: props.modelYearId});
  const modelQuery = useGetModelQuery(
    {modelId: yearQuery.data?.modelId ?? ''},
    {skip: !yearQuery.data}
  );
  const makeQuery = useGetMakeQuery(
    {makeId: modelQuery.data?.makeId ?? ''},
    {skip: !modelQuery.data}
  );
  const isLoading = yearQuery.isLoading || modelQuery.isLoading || makeQuery.isLoading;
  const isError =
    yearQuery.isError || (!isLoading && (!yearQuery.data || !modelQuery.data || !makeQuery.data));

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      errorMessage={t('entity.newVehicle.errors.modelYearNotFound', 'Model year not found')}
      errorAction={{
        title: t('entity.newVehicle.actions.backToCatalogue', 'Back to catalogue'),
        onClick: () => navigate(vehiclesRoutes.newVehicleCatalogue),
        variant: 'secondary',
      }}
    >
      <Show
        when={
          yearQuery.data && modelQuery.data && makeQuery.data
            ? {year: yearQuery.data, model: modelQuery.data, make: makeQuery.data}
            : null
        }
      >
        {(vehicle) => (
          <NewVehicleConfigurator
            mode="preview"
            draftKey="newVehiclePreview"
            deepLink={{modelYearId: props.modelYearId}}
            previewContext={{
              makeId: vehicle.make.id,
              makeName: vehicle.make.name,
              modelId: vehicle.model.id,
              modelName: vehicle.model.name,
              modelYearName: vehicle.year.name,
              status: vehicle.year.status,
              version: vehicle.year.version,
            }}
          />
        )}
      </Show>
    </DataStatus>
  );
}

function ConfiguratorLoader(props: ConfiguratorLoaderProps) {
  const {data: user} = useGetCurrentUserInfoQuery();

  if (!user) {
    return <Text>{t('general.labels.loading', 'Loading…')}</Text>;
  }

  return <UserConfiguratorLoader key={user.id} {...props} userId={user.id} />;
}

export function NewVehicleShell() {
  const location = useLocation();
  const {makeId, modelId, modelYearId: routeModelYearId} = useParams();
  const query = new URLSearchParams(location.search);
  const configurationId = query.get('configurationId') ?? undefined;
  const currentConfigurationId = useLatest(configurationId);
  const businessCaseId = query.get('businessCaseId') ?? undefined;
  const replaceSaleVehicleId = query.get('replaceSaleVehicleId') ?? undefined;
  const [getConfigurationDetail] = useLazyGetConfigurationDetailQuery();
  const isMounted = useRef(true);
  const detailRequestSequence = useRef(0);
  const openOrderConfiguration = (
    targetConfigurationId: string,
    isIntentCurrent: () => boolean
  ) => {
    if (!isMounted.current || currentConfigurationId.current !== targetConfigurationId) {
      return;
    }
    const sequence = detailRequestSequence.current + 1;
    detailRequestSequence.current = sequence;
    void getConfigurationDetail({configurationId: targetConfigurationId}, false)
      .unwrap()
      .then((configuration) => {
        if (
          !isMounted.current ||
          detailRequestSequence.current !== sequence ||
          currentConfigurationId.current !== targetConfigurationId ||
          !isIntentCurrent()
        ) {
          return;
        }
        if (configuration.order || configuration.vehicle) {
          showNotification.warning(
            t('entity.newVehicle.order.alreadyOrdered', 'This configuration is already ordered')
          );
          return;
        }
        const dialogId = getOrderConfigurationDialogId(targetConfigurationId);
        openDialog(
          <OrderConfigurationDialog
            configuration={configuration}
            targetBusinessCaseId={businessCaseId}
            dialogId={dialogId}
            // Carried from the Business Case that sent the user here to replace its vehicle; the
            // commit swaps the selection instead of demanding an empty slot.
            replacement={
              replaceSaleVehicleId ? {expectedSaleVehicleId: replaceSaleVehicleId} : undefined
            }
          />,
          {
            id: dialogId,
            size: 'large',
            title: t('entity.newVehicle.order.title', 'Order configuration'),
          }
        );
      })
      .catch(handleApiError);
  };
  useOnMount(
    () => {
      isMounted.current = true;
    },
    () => {
      isMounted.current = false;
    }
  );
  const registerExternalOrder = (targetConfigurationId: string, isIntentCurrent: () => boolean) => {
    if (!isMounted.current || currentConfigurationId.current !== targetConfigurationId) {
      return;
    }
    const sequence = detailRequestSequence.current + 1;
    detailRequestSequence.current = sequence;
    void getConfigurationDetail({configurationId: targetConfigurationId}, false)
      .unwrap()
      .then((configuration) => {
        if (
          !isMounted.current ||
          detailRequestSequence.current !== sequence ||
          currentConfigurationId.current !== targetConfigurationId ||
          !isIntentCurrent()
        ) {
          return;
        }
        if (configuration.order || configuration.vehicle) {
          showNotification.warning(
            t('entity.newVehicle.order.alreadyOrdered', 'This configuration is already ordered')
          );
          return;
        }
        const dialogId = getRegisterExternalOrderDialogId(targetConfigurationId);
        openDialog(
          <RegisterExternalOrderDialog configuration={configuration} dialogId={dialogId} />,
          {
            id: dialogId,
            size: 'large',
            title: t(
              'entity.newVehicle.external.title',
              'Register as externally ordered / already stocked'
            ),
          }
        );
      })
      .catch(handleApiError);
  };
  const interestId = query.get('interestId') ?? undefined;
  const modelYearId = query.get('modelYearId') ?? undefined;
  const trimId = query.get('trimId') ?? undefined;
  const isConfigurator = location.pathname.includes('/create-new-vehicle/');
  const isPreview = location.pathname.includes('/preview/step/');

  return (
    <Main>
      {/* eslint-disable-next-line no-nested-ternary -- Pre-existing violation */}
      {isPreview && routeModelYearId ? (
        <PreviewConfiguratorLoader modelYearId={routeModelYearId} />
      ) : // eslint-disable-next-line no-nested-ternary -- Pre-existing violation
      isConfigurator ? (
        <ConfiguratorLoader
          key={configurationId ?? 'create'}
          configurationId={configurationId}
          businessCaseId={businessCaseId}
          replaceSaleVehicleId={replaceSaleVehicleId}
          interestId={interestId}
          modelYearId={modelYearId}
          trimId={trimId}
          onOrderConfiguration={openOrderConfiguration}
          onRegisterExternalOrder={registerExternalOrder}
        />
      ) : // eslint-disable-next-line no-nested-ternary -- Pre-existing violation
      location.pathname.includes('/wizard/new-model/step/') ? (
        <NewModelWizard />
      ) : // eslint-disable-next-line no-nested-ternary -- Pre-existing violation
      routeModelYearId ? (
        <ModelYearShell />
      ) : // eslint-disable-next-line no-nested-ternary -- Pre-existing violation
      modelId ? (
        <ModelDetail />
      ) : makeId ? (
        <MakeDetail />
      ) : (
        <NewVehicleCatalogue />
      )}
    </Main>
  );
}
